import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import Select from '../Elements/Select/Select';
import LazyImage from '../../hocs/Lazy/LazyImage';
import { changeCountry, changeAddressInput } from '../../actions/checkout';
import { resetFilter } from '../../actions/environment';
import { addEvent, removeEvent } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { translate, prefix } from '../../utils/locale';
import { urlify } from '../../utils/string';
import s from './Navigation.scss';

const Navigation = ({ navigation, setup, menuPopupHeight, loggedIn, closeSearch, ...props }) => {
	const [navigationItemExpanded, expandNavigationItem] = useState({
		level0: null,
		level1Top: null,
		level1Center: null,
		level1Bottom: null,
	});
	const navigationInner = useRef(null);

	useEffect(() => {
		addEvent(document.querySelector('body'), 'click.navigationClose', (e) => {
			if (navigationInner.current && !navigationInner.current.contains(e.target) && Object.keys(navigationItemExpanded).filter((open) => navigationItemExpanded[open] !== null).length > 0) {
				e.preventDefault();
				e.stopPropagation();
				expandNavigationItem({
					level0: null,
					level1Top: null,
					level1Center: null,
					level1Bottom: null,
				});
			}
		});

		addEvent(window, 'keydown.navigationClose', (e) => {
			if (e.key === 'Escape') {
				expandNavigationItem({
					level0: null,
					level1Top: null,
					level1Center: null,
					level1Bottom: null,
				});
			}
		});

		return () => {
			removeEvent(document.querySelector('body'), 'click.navigationClose');
			removeEvent(window, 'keydown.navigationClose');
		};
	});

	const resetFilterHandler = (e) => {
		const target = e.currentTarget.getAttribute('href');
		if (/https:\/\/(archive\.)?jeanerica\.com/.test(target)) {
			window.location = target;
			return;
		}
		props.resetFilter();
		closeSearch();
		expandNavigationItem({
			level0: null,
			level1Top: null,
			level1Center: null,
			level1Bottom: null,
		});
	};

	// Remove countries not shipped to.
	const countries = Object.keys(setup.countries).reduce((acc, country) => (country !== '0' && setup.countries[country].shipTo ? [...acc, { key: country, value: setup.countries[country].name }] : [...acc]), []);
	const onCountryChange = (id, value) => {
		props.changeCountry(value);
		props.changeAddressInput('address', 'country', value);
		props.changeAddressInput('shippingAddress', 'country', value);
	};

	const expandNavigationHandler = (e) => {
		const id = e.currentTarget.id;
		const level = id.match(/(level\d\w*)/)[1];
		const index = parseInt(id.match(/-(\d+)/)[1], 10);

		closeSearch();

		if (level === 'level0') e.preventDefault();

		if (level === 'level0' && (navigationItemExpanded.level1Top !== null || navigationItemExpanded.level1Center !== null || navigationItemExpanded.level1Bottom !== null)) {
			expandNavigationItem((prevState) => ({
				...prevState,
				level1Top: null,
				level1Center: null,
				level1Bottom: null,
			}));
		} else if (navigationItemExpanded[level] !== index) {
			e.preventDefault();
			expandNavigationItem((prevState) => ({
				...prevState,
				level1Top: null,
				level1Center: null,
				level1Bottom: null,
				[level]: index,
			}));
		} else if (level !== 'level0' && navigationItemExpanded[level] === index) {
			e.preventDefault();
			expandNavigationItem({
				level1Top: null,
				level1Center: null,
				level1Bottom: null,
			});
		} else {
			expandNavigationItem({
				level0: null,
				level1Top: null,
				level1Center: null,
				level1Bottom: null,
			});
		}
	};

	const mobileNavigationSection = (subNavigation, position) => (
		subNavigation && subNavigation.map((level1, i) => {
			const level1URI = translate(setup, level1, 'uri');
			const level1Title = translate(setup, level1, 'title');
			const externalLink = /https:\/\/(archive\.)?jeanerica\.com/.test(level1URI);
			const combinedURILevel1 = externalLink ? level1URI : prefix(setup, level1URI);
			const level2SubCategoryLinks = [];
			const styles1 = level1?.styles || {};

			if (level1.subPages) {
				level1.subPages.sort((a, b) => {
					if (a.inclusion === 'manual' && b.inclusion === 'dynamic') return 1;
					if (b.inclusion === 'manual' && a.inclusion === 'dynamic') return -1;
					return 0;
				}).forEach((level2, j) => {
					const level2URI = translate(setup, level2, 'uri');
					const level2Title = translate(setup, level2, 'title');
					const combinedURILevel2 = prefix(setup, level2URI);
					const styles2 = level2?.styles || styles1 || {};

					if (level2.title.indexOf('<<<') !== -1) {
						level2SubCategoryLinks.push((
							<div key={urlify(level2.title)} className={s.level2_block}>
								<div className={s.level2_block_item}>
									<span className={s.level2_block_item_link} />
								</div>
							</div>
						));
					} else {
						level2SubCategoryLinks.push((
							<div key={urlify(combinedURILevel2)} className={s.level2_block}>
								<div className={s.level2_block_item}>
									<Link id={`level2${position}-${j}`} onClick={resetFilterHandler} className={s.level2_block_item_link} to={combinedURILevel2} style={styles2}><span>{ level2Title }</span></Link>
								</div>
							</div>
						));
					}
				});
			}

			return (
				<div key={urlify(combinedURILevel1)} className={[s.level1_column, (navigationItemExpanded[`level1${position}`] === i ? s.level1_column__expanded : null)].join(' ')}>
					<Link id={`level1${position}-${i}`} className={s.level1_column_link} to={combinedURILevel1} onClick={level1.subPages ? expandNavigationHandler : resetFilterHandler} style={styles1}>
						{
							level1.icon ? (
								<LazyImage {...imageSizeTranslation('3', level1.icon)} />
							) : (
								level1Title.split('(').map((titlePart, k) => <span key={urlify(titlePart)}>{`${k > 0 ? '(' : ''}${titlePart}`}</span>)
							)
						}
						{
							level1.subPages && (
								<svg xmlns="http://www.w3.org/2000/svg" width="9" height="9" viewBox="0 0 9 9">
									<g fill="none" fillRule="evenodd" strokeLinecap="round">
										{ navigationItemExpanded[`level1${position}`] !== i && <line x1="4.5" x2="4.5" y1="1.5" y2="7.5" stroke={styles1.color || '#000'} /> }
										<line x1="7.5" x2="1.5" y1="4.506" y2="4.506" stroke={styles1.color || '#000'} />
									</g>
								</svg>
							)
						}
					</Link>
					{
						level1.subPages && (
							<div className={s.level1_columnInner}>
								<div className={s.level2}>
									{ level2SubCategoryLinks }
								</div>
							</div>
						)
					}
				</div>
			);
		})
	);

	return (
		<nav className={s.navigation} ref={navigationInner}>
			<a id="level0-0" className={[s.mobile, (navigationItemExpanded.level0 !== null ? s.mobile__expanded : null)].join(' ')} href="#open-mobile-menu" aria-label="Open mobile menu" onClick={expandNavigationHandler}>
				<span /><span />
			</a>
			<div className={[s.level1, s.level1__mobile].join(' ')} style={{ height: `${navigationItemExpanded.level0 !== null ? menuPopupHeight : 0}px` }}>
				<div className={s.level1_columns}>
					{ mobileNavigationSection(navigation.headerTopMobile, 'Top') }
				</div>
				<div className={s.level1_columns}>
					{ mobileNavigationSection(navigation.headerCenterMobile, 'Center') }
				</div>
				<div className={s.level1_columns}>
					{ mobileNavigationSection(navigation.headerBottomMobile, 'Bottom') }
					<Link onClick={resetFilterHandler} className={[s.level1_column, s.level1_column__function].join(' ')} to={prefix(setup, 'account')}>
						<span>{ translate(setup, 'headerAccount') }: </span>
						<span>{ loggedIn ? translate(setup, 'headerView') : translate(setup, 'headerSignIn') }</span>
					</Link>
					<div className={[s.level1_column, s.level1_column__function].join(' ')} style={{ width: `${Math.ceil((setup.countries[setup.country].name.length + 9) * 8.5)}px` }}>
						<Select id="countryMobile" selected={setup.country} options={countries} inputChange={onCountryChange} selectedPrefix={`${translate(setup, 'labelShippingTo')}: `} />
					</div>
				</div>
			</div>
			<div className={[s.level1, s.level1__desktop].join(' ')}>
				{
					navigation.header && navigation.header.map((level1, i) => {
						const level1URI = translate(setup, level1, 'uri');
						const combinedURILevel1 = prefix(setup, level1URI);
						const level2SubCategoryLinks = [];
						const styles1 = level1?.styles || {};

						if (level1.subPages) {
							level1.subPages.sort((a, b) => {
								if (a.inclusion === 'manual' && b.inclusion === 'dynamic') return 1;
								if (b.inclusion === 'manual' && a.inclusion === 'dynamic') return -1;
								return 0;
							}).forEach((level2, j) => {
								const level2URI = translate(setup, level2, 'uri');
								const level2Title = translate(setup, level2, 'title');
								const externalLink = /https:\/\/(archive\.)?jeanerica\.com/.test(level2URI);
								const combinedURILevel2 = externalLink ? level2URI : prefix(setup, level2URI);
								const styles2 = level2?.styles || styles1 || {};

								if (level2.title.indexOf('<<<') !== -1) {
									level2SubCategoryLinks.push((
										<div key={urlify(level2.title)} className={s.level2_block}>
											<div className={s.level2_block_item}>
												<span className={s.level2_block_item_link} />
											</div>
										</div>
									));
								} else {
									level2SubCategoryLinks.push((
										<div key={urlify(combinedURILevel2)} className={[s.level2_block, (level2.inclusion === 'manual' && level2.type !== 'category' ? s.level2_block__subtitle : s.level2_block__standard)].join(' ')}>
											<div className={s.level2_block_item}>
												<Link id={`level2-${j}`} onClick={resetFilterHandler} className={[s.level2_block_item_link, (level2?.icon?.url && level2.icon.url.indexOf('.svg') !== -1 ? s.level2_block_item_link__svg : null)].join(' ')} to={combinedURILevel2} style={styles2}>
													{
														level2.icon ? (
															<LazyImage {...imageSizeTranslation('3', level2.icon)} />
														) : (
															level2Title.split('(').map((titlePart, k) => <span key={urlify(titlePart)}>{`${k > 0 ? '(' : ''}${titlePart}`}</span>)
														)
													}
												</Link>
											</div>
										</div>
									));
								}
							});
						}

						return (
							<div key={urlify(combinedURILevel1)} className={[s.level1_column, (navigationItemExpanded.level1Top === i ? s.level1_column__expanded : null)].join(' ')}>
								<Link id={`level1Top-${i}`} className={s.level1_column_link} to={combinedURILevel1} onClick={level1.subPages ? expandNavigationHandler : resetFilterHandler} style={styles1}>
									<span>{ translate(setup, level1, 'title') }</span>
								</Link>
								{
									level1.subPages && (
										<div className={s.level1_columnInner}>
											<div className={s.level2}>
												{ level2SubCategoryLinks }
											</div>
										</div>
									)
								}
							</div>
						);
					})
				}
			</div>
			<div className={s.navigation_screen} />
		</nav>
	);
};

const mapState = (state, props) => (props);

export default hot(module)(connect(mapState, { resetFilter, changeCountry, changeAddressInput })(Navigation));
